function formatMoney(cents, format = "${{amount}}") {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number === null) {
      return 0;
    }

    number = (number / 100).toFixed(precision);

    const split = number.split('.');
    const dollars = split[0];
    const centsPart = split[1];
    const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
    const formattedCents = centsPart ? decimal + centsPart : '';

    return formattedDollars + formattedCents;
  };

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholderRegex);

  if (!match) {
    return format;
  }

  const placeholder = match[1];
  let value;

  switch (placeholder) {
    case 'amount':
      value = formatWithDelimiters(cents);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = '';
  }

  return format.replace(placeholderRegex, value);
}

class BuildABoxDynamicPricing {
  constructor(config) {
    this.config = config || {};
    this.selectedSellingPlan = config.selected_selling_plan;
    this.sellingPlans = config.selling_plans || [];
    this.bundleProduct = config.bundle_product;
    this.bundleVariant = config.bundle_variant;
    this.rechargeBundle = null;
    this.initRechargeBundle();
  }

  initRechargeBundle() {
    if (this.rechargeBundle) return;
    if (typeof window.recharge !== 'undefined' && window.recharge.bundle) {
      this.rechargeBundle = window.recharge.bundle;
    } else {
      this.rechargeBundle = null;
    }
  }

  updateSelectedSellingPlan(sellingPlanId) {
    let sellingPlan = null;
    if (this.bundleVariant && Array.isArray(this.bundleVariant.selling_plan_allocations)) {
      const allocation = this.bundleVariant.selling_plan_allocations.find(a => a && a.selling_plan && String(a.selling_plan.id) === String(sellingPlanId));
      if (allocation) sellingPlan = allocation.selling_plan;
    }
    if (!sellingPlan) {
      sellingPlan = (this.sellingPlans || []).find(p => String(p.id) === String(sellingPlanId));
    }
    if (sellingPlan) this.selectedSellingPlan = sellingPlan;
  }

  async createRechargeBundle(session, sellingPlanId = null) {
    this.initRechargeBundle();
    if (!this.rechargeBundle) {
      throw new Error('Recharge bundle library not available');
    }
    if (!this.bundleProduct?.id || !this.bundleVariant?.id) {
      throw new Error('Bundle product or variant not properly configured');
    }

    const planId = sellingPlanId || this.selectedSellingPlan?.id;
    let sellingPlan = null;
    if (this.bundleVariant?.selling_plan_allocations) {
      const allocation = this.bundleVariant.selling_plan_allocations.find(alloc => alloc?.selling_plan?.id == planId);
      if (allocation) sellingPlan = allocation.selling_plan;
    }
    if (!sellingPlan) {
      sellingPlan = (this.sellingPlans || []).find(plan => plan.id == planId);
    }
    if (!sellingPlan) {
      throw new Error(`Selling plan not found for ID: ${planId}`);
    }

    const selections = this.prepareBundleSelections(session, sellingPlan);
    if (!selections || selections.length === 0) {
      throw new Error('No bundle selections prepared');
    }

    const bundleData = {
      externalProductId: this.bundleProduct.id.toString(),
      externalVariantId: this.bundleVariant.id.toString(),
      selections
    };

    const validation = await this.rechargeBundle.validateDynamicBundle(bundleData);
    if (validation !== true) {
      throw new Error(`Bundle validation failed: ${validation}`);
    }

    const items = await this.rechargeBundle.getDynamicBundleItems(bundleData, this.bundleProduct.handle);
    return items;
  }

  prepareBundleSelections(session, sellingPlan) {
    const selections = [];
    const products = (window.BuildABoxProductData && Array.isArray(window.BuildABoxProductData.products)) ? window.BuildABoxProductData.products : [];

    let intervalFrequency = sellingPlan.delivery_policy?.interval_frequency;
    let intervalUnit = sellingPlan.delivery_policy?.interval_unit;
    if (!intervalFrequency || !intervalUnit) {
      const daysFromName = this.extractDaysFromPlanName(sellingPlan.name);
      if (daysFromName) {
        intervalFrequency = daysFromName;
        intervalUnit = 'day';
      } else {
        intervalFrequency = 1;
        intervalUnit = 'month';
      }
    }

    const items = Array.isArray(session?.selected_products) ? session.selected_products : [];
    for (const sel of items) {
      const product = products.find(p => String(p.id) === String(sel.product_id || sel.id));
      if (!product) continue;
      const variantId = sel.variant_id || product.first_available_variant?.id || product.id;
      const productSellingPlan = this.findMatchingSellingPlan(product, intervalFrequency, intervalUnit);
      if (!variantId || !productSellingPlan) continue;
      selections.push({
        collectionId: product.collections?.[0]?.id?.toString() || '',
        externalProductId: (sel.product_id || product.id).toString(),
        externalVariantId: variantId.toString(),
        quantity: sel.quantity || 1,
        sellingPlan: productSellingPlan.id,
        shippingIntervalFrequency: intervalFrequency,
        shippingIntervalUnitType: intervalUnit,
        discountedVariantId: variantId
      });
    }

    return selections;
  }

  findMatchingSellingPlan(product, targetIntervalFrequency, targetIntervalUnit) {
    if (!product?.selling_plan_groups || product.selling_plan_groups.length === 0) {
      return null;
    }
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        for (const plan of group.selling_plans) {
          const planFrequency = plan.delivery_policy?.interval_frequency;
          const planUnit = plan.delivery_policy?.interval_unit;
          if (planFrequency == targetIntervalFrequency && planUnit === targetIntervalUnit) {
            return plan;
          }
        }
      }
    }
    let targetDays;
    if (targetIntervalUnit === 'day' || targetIntervalUnit === 'days') {
      targetDays = targetIntervalFrequency;
    } else {
      targetDays = this.convertToTotalDays(targetIntervalFrequency, targetIntervalUnit);
    }
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        for (const plan of group.selling_plans) {
          const planDays = this.extractDaysFromPlanName(plan.name);
          if (planDays === targetDays) {
            return plan;
          }
        }
      }
    }
    for (const group of product.selling_plan_groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        return group.selling_plans[0];
      }
    }
    return null;
  }

  convertToTotalDays(frequency, unit) {
    switch(unit) {
      case 'day':
      case 'days':
        return frequency;
      case 'week':
      case 'weeks':
        return frequency * 7;
      case 'month':
      case 'months':
        return frequency * 30;
      default:
        return frequency;
    }
  }

  extractDaysFromPlanName(planName) {
    if (!planName) return null;
    const dayMatch = planName.match(/(?:every\s+)?(\d+)\s*days?/i);
    if (dayMatch) return parseInt(dayMatch[1]);
    const weekMatch = planName.match(/(?:every\s+)?(\d+)\s*weeks?/i);
    if (weekMatch) return parseInt(weekMatch[1]) * 7;
    const monthMatch = planName.match(/(?:every\s+)?month(?:ly)?/i);
    if (monthMatch) return 30;
    return null;
  }
}

class BuildABoxStateManager {
  constructor() {
    this.storageKey = 'naturaw_build_a_box_state';
    this.eventBus = this.createEventBus();
    this.state = this.loadState();
  }

  createEventBus() {
    const events = {};
    return {
      on: (event, callback) => {
        if (!events[event]) events[event] = [];
        events[event].push(callback);
      },
      emit: (event, data) => {
        if (events[event]) {
          events[event].forEach(callback => callback(data));
        }
      },
      off: (event, callback) => {
        if (events[event]) {
          events[event] = events[event].filter(cb => cb !== callback);
        }
      }
    };
  }

  getDefaultState() {
    return {
      box_id: `box_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      created_at: new Date().toISOString(),
      last_updated: new Date().toISOString(),
      selected_products: [],
      current_filter: 'all',
      minimum_required: 8,
      postage_savings_threshold: 16,
      general_types_tags: [],
      box_sizes: [8, 16, 30, 46, 60, 76, 90, 106, 120, 136, 150, 166, 180, 196],
      delivery_type: 'subscription',
      subscription_frequency: 'weekly',
      subscription_discount: 5,
      selected_selling_plan_id: null,
      total_items: 0,
      subtotal: 0,
      discount_amount: 0,
      final_total: 0,
      main_product: null,
      sidebar_visible: false,
      delivery_modal_open: false,
      sidebar_products_open: false,
      reminder_state: {
        treats_shown: false,
        boosts_shown: false
      }
    };
  }

  loadState() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const state = { ...this.getDefaultState(), ...parsed };
        this.eventBus.emit('state:loaded', state);
        return state;
      }
    } catch (e) {
      console.warn('[BuildABox] Failed to load state:', e);
    }
    
    const newState = this.getDefaultState();
    this.eventBus.emit('state:created', newState);
    return newState;
  }

  saveState(state = this.state) {
    try {
      state.last_updated = new Date().toISOString();
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      this.state = state;
      this.eventBus.emit('state:saved', state);
    } catch (e) {
      console.error('[BuildABox] Failed to save state:', e);
      this.eventBus.emit('state:save_error', e);
    }
  }

  getState() {
    return this.state;
  }

  updateState(updates) {
    const previousState = { ...this.state };
    this.state = { ...this.state, ...updates };
    this.saveState();
    
    this.eventBus.emit('state:updated', {
      previous: previousState,
      current: this.state,
      changes: updates
    });
    
    return this.state;
  }

  updateField(field, value) {
    const updates = {};
    updates[field] = value;
    return this.updateState(updates);
  }

  addProduct(product, quantity = 1) {
    const existingProductIndex = this.state.selected_products.findIndex(p => p.id === product.id);
    
    if (existingProductIndex > -1) {
      this.state.selected_products[existingProductIndex].quantity += quantity;
    } else {
      this.state.selected_products.push({
        ...product,
        quantity,
        added_at: new Date().toISOString()
      });
    }
    
    this.recalculateTotals();
    this.saveState();
    this.eventBus.emit('product:added', { product, quantity });
  }

  removeProduct(productId, quantity = 1) {
    const existingProductIndex = this.state.selected_products.findIndex(p => p.id === productId);
    
    if (existingProductIndex > -1) {
      const product = this.state.selected_products[existingProductIndex];
      product.quantity -= quantity;
      
      if (product.quantity <= 0) {
        this.state.selected_products.splice(existingProductIndex, 1);
        this.eventBus.emit('product:removed', { productId });
      } else {
        this.eventBus.emit('product:quantity_updated', { productId, quantity: product.quantity });
      }
    }
    
    this.recalculateTotals();
    this.saveState();
  }

  recalculateTotals() {
    let baseTotalCents = 0;
    let totalItems = 0;

    const getUnitPriceCents = (product) => {
      const raw = typeof product?.price === 'number' ? product.price : parseFloat(product?.price || 0);
      return Number.isNaN(raw) ? 0 : Math.round(raw);
    };

    const computeDiscountedUnitCents = (unitCents, discountPercentage) => {
      const dollars = unitCents / 100;
      const discountedDollars = dollars * (1 - (discountPercentage || 0) / 100);
      const roundedDollars = Math.round(discountedDollars * 100) / 100;
      return Math.round(roundedDollars * 100);
    };

    let discountedTotalCents = 0;
    const isSubscription = this.state.delivery_type === 'subscription';
    const discountPercentage = isSubscription ? (this.state.subscription_discount || 0) : 0;

    this.state.selected_products.forEach((product) => {
      const qty = product.quantity || 1;
      const unitCents = getUnitPriceCents(product);
      baseTotalCents += unitCents * qty;
      totalItems += qty;
      if (isSubscription) {
        const discountedUnit = computeDiscountedUnitCents(unitCents, discountPercentage);
        discountedTotalCents += discountedUnit * qty;
      }
    });

    const finalTotalCents = isSubscription ? discountedTotalCents : baseTotalCents;
    const discountAmountCents = Math.max(0, baseTotalCents - finalTotalCents);

    this.state.subtotal = baseTotalCents;
    this.state.total_items = totalItems;
    this.state.discount_amount = discountAmountCents;
    this.state.final_total = finalTotalCents;

    this.eventBus.emit('totals:recalculated', {
      subtotal: baseTotalCents,
      totalItems,
      discountAmount: discountAmountCents,
      finalTotal: finalTotalCents
    });
  }

  getForageOriginalCount() {
    return this.getEligibleSelectedCount();
  }

  getEligibleSelectedCount() {
    const allowedTypes = Array.isArray(this.state.general_types_tags) ? this.state.general_types_tags : [];
    if (!allowedTypes.length) {
      return this.state.selected_products.reduce((sum, product) => sum + product.quantity, 0);
    }
    return this.state.selected_products.reduce((sum, product) => {
      return allowedTypes.includes(product.type) ? sum + product.quantity : sum;
    }, 0);
  }

  isMinimumMet() {
    return this.getForageOriginalCount() >= this.state.minimum_required;
  }

  getNextBoxSize() {
    const eligibleCount = this.getEligibleSelectedCount();
    if (eligibleCount < 8) return 8;
    if (eligibleCount < 16) return 16;
    const nextMultipleOf30 = Math.ceil((eligibleCount + 1) / 30) * 30;
    const nextSixteenSeries = 16 + Math.ceil(((eligibleCount + 1) - 16) / 30) * 30;
    return Math.min(nextMultipleOf30, nextSixteenSeries);
  }

  clearState() {
    this.state = this.getDefaultState();
    this.saveState();
    this.eventBus.emit('state:cleared', this.state);
  }
}

if (!customElements.get('build-a-box')) {

class BuildABox extends HTMLElement {
    
    constructor() {
        super();
        this.stateManager = new BuildABoxStateManager();
        this.state = this.stateManager.getState();
        this.productData = window.BuildABoxProductData || { 
            products: [], 
            product_types: [], 
            box_sizes: '16,30', 
            minimum_number_of_boxes: 8 
        };
        this.moneyFormat = (window.theme && window.theme.moneyFormat) || (window.shop && window.shop.money_format) || (window.quizInfo && window.quizInfo.money_format) || '${{amount}}';
        this.dynamicPricing = new BuildABoxDynamicPricing({
            selected_selling_plan: this.productData?.selected_selling_plan || null,
            selling_plans: this.productData?.selling_plans || [],
            bundle_product: (window.BuildABoxProductData && window.BuildABoxProductData.main_product) || this.productData?.main_product || null,
            bundle_variant: (window.BuildABoxProductData && window.BuildABoxProductData.main_variant) || this.productData?.main_variant || null
        });
        this.translations = {};
        this.loadTranslations();
        this.init();
    }

    isMobile() {
        return window.innerWidth < 990;
    }

    loadTranslations() {
        const translationScript = document.querySelector('script[data-build-a-box-translations]');
        if (translationScript) {
            try {
                const content = translationScript.textContent.trim();
                if (content) {
                    this.translations = JSON.parse(content);
                    console.log('[BuildABox] Loaded translations:', Object.keys(this.translations));
                } else {
                    console.warn('[BuildABox] Empty translation script content');
                }
            } catch (e) {
                console.warn('[BuildABox] Failed to load translations:', e);
                console.warn('Translation script content:', translationScript.textContent);
            }
        } else {
            console.warn('[BuildABox] Translation script not found');
            
            if (!Object.keys(this.translations).length) {
                fetch('/locales/en.default.json')
                    .then(response => response.json())
                    .then(data => {
                        this.translations = data.build_a_box || {};
                        console.log('[BuildABox] Fallback translations loaded:', Object.keys(this.translations));
                    })
                    .catch(e => console.warn('Failed to fetch fallback translations:', e));
            }
        }
    }

    getTranslation(key, replacements = {}) {
        const value = this.translations[key] || key;
        
        if (typeof value === 'string') {
            let result = value;
            for (const [key, replacement] of Object.entries(replacements)) {
                result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), replacement);
            }
            return result;
        }
        
        return value;
    }

    renderSubscriptionBenefits() {
        const benefits = [
            { key: 'change_box_contents', text: this.getTranslation('change_box_contents') },
            { key: 'loyalty_rewards', text: this.getTranslation('loyalty_rewards') },
            { key: 'pause_cancel_anytime', text: this.getTranslation('pause_cancel_anytime') }
        ];

        const validBenefits = benefits.filter(benefit => 
            benefit.text && 
            benefit.text.trim() !== '' && 
            benefit.text !== benefit.key
        );

        if (validBenefits.length === 0) {
            return '';
        }

        const benefitsHTML = validBenefits.map(benefit => `
            <div class="build-a-box__subscription-benefit">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none">
                    <path d="M4 9.69981L0 5.69981L1.4 4.29981L4 6.89981L10.6 0.299805L12 1.6998L4 9.69981Z" fill="#EF578A"/>
                </svg>
                <span>${benefit.text}</span>
            </div>
        `).join('');

        return `
            <div class="build-a-box__subscription-benefits">
                ${benefitsHTML}
            </div>
        `;
    }

    init() {
        this.setupEventListeners();
        this.render();
        this.bindEvents();
        
        this.stateManager.updateState({
            minimum_required: this.productData.minimum_number_of_boxes || 8,
            postage_savings_threshold: this.productData.minimum_number_of_boxes_for_save_on_postage || 16,
            general_types_tags: (this.productData.tags_of_general_types || '')
              .split(',')
              .map(s => s.trim())
              .filter(Boolean),
        });

        // ensure bottom sheet is closed on fresh load
        this.stateManager.updateState({ sidebar_visible: false, delivery_modal_open: false });

        if (this.productData?.selling_plans && this.productData.selling_plans.length > 0) {
            const defaultPlanId = this.productData?.selected_selling_plan?.id || this.productData?.selling_plans?.[0]?.id || null;
            if (defaultPlanId) {
                const discount = this.getDiscountPercentForPlanId(defaultPlanId) ?? this.state.subscription_discount;
                this.stateManager.updateState({ 
                    selected_selling_plan_id: defaultPlanId, 
                    subscription_discount: discount
                });
                this.dynamicPricing.updateSelectedSellingPlan(defaultPlanId);
            }
        }
        
        this.stateManager.recalculateTotals();
        
        // Update product cards to restore input values from state
        this.updateProductCards();
    }

    setupEventListeners() {
        this.stateManager.eventBus.on('state:updated', (data) => {
            this.state = data.current;
            this.updateUI(data.changes);
        });

        this.stateManager.eventBus.on('product:added', () => {
            this.updateProductCards();
            this.updateSidebar();
            this.updateMessage();
        });

        this.stateManager.eventBus.on('product:removed', () => {
            this.updateProductCards();
            this.updateSidebar();
            this.updateMessage();
        });

        this.stateManager.eventBus.on('product:quantity_updated', () => {
            this.updateProductCards();
            this.updateSidebar();
            this.updateMessage();
        });

        this.stateManager.eventBus.on('totals:recalculated', () => {
            this.updatePricing();
        });
    }

    render() {
        // Рендеримо компоненти в окремі контейнери
        this.renderToContainer('.build-a-box__container-title-container', this.renderContainerTitle());
        this.renderToContainer('.build-a-box__message-container.large-up-hide', this.renderMessageWithTitle());
        this.renderToContainer('.build-a-box__tabs-container', this.renderTabs());  
        this.renderToContainer('.build-a-box__products-container', this.renderProductGrid());
        this.renderToContainer('.build-a-box__sidebar-container', this.renderSidebar());
    }

    renderToContainer(selector, content) {
        const container = this.querySelector(selector);
        if (container) {
            container.innerHTML = content;
        } else {
            console.warn(`[BuildABox] Container not found: ${selector}`);
        }
    }

    renderContainerTitle() {
        return `
            <div class="build-a-box__container-title">
                <h2 class="build-a-box__container-title-text">${this.getTranslation('container_title')}</h2>
            </div>
        `;
    }

    renderMessageWithTitle() {
        return `
            <div class="build-a-box__container-title">
                <h2 class="build-a-box__container-title-text">${this.getTranslation('container_title')}</h2>
                ${this.renderMessage()}
            </div>
        `;
    }

    renderMessage() {
        const forageOriginalCount = this.stateManager.getForageOriginalCount();
        const isMinimumMet = this.stateManager.isMinimumMet();
        const nextBoxSize = this.stateManager.getNextBoxSize();
        const eligibleCount = this.stateManager.getEligibleSelectedCount();
        const remainingToNext = nextBoxSize ? nextBoxSize - eligibleCount : 0;
        const isUnderEight = eligibleCount < 8;
        const isUnderSixteen = eligibleCount >= 8 && eligibleCount < 16;
        const remainingToEight = Math.max(0, 8 - eligibleCount);
        const remainingToSixteen = Math.max(0, 16 - eligibleCount);
        const isExactlyEight = eligibleCount === 8;
        const isExactlySixteen = eligibleCount === 16;
        const isThirtyMultiple = eligibleCount >= 30 && eligibleCount % 30 === 0;
        const isSixteenOffsetMultiple = eligibleCount > 16 && ((eligibleCount - 16) % 30 === 0);
        const isGreen = isExactlyEight || isExactlySixteen || isThirtyMultiple || isSixteenOffsetMultiple;
        
        let messageTitle = '';
        let messageText = '';
        
        if (isUnderEight) {
            messageTitle = this.getTranslation('minimum_required', { count: remainingToEight });
        } else if (isExactlyEight || isExactlySixteen || isThirtyMultiple || isSixteenOffsetMultiple) {
            messageTitle = this.getTranslation('great_you_filled_box_message');
        } else if (isUnderSixteen) {
            messageTitle = this.getTranslation('add_minces_message', { count: remainingToSixteen });
        } else if (nextBoxSize && remainingToNext > 0) {
            messageTitle = this.getTranslation('size_up_save_delivery_message', { count: remainingToNext });
        } else {
            messageTitle = this.getTranslation('you_filled_box_plain');
        }
        
        messageText = this.getTranslation('ordering_info');

        return `
            <div class="build-a-box__message ${isGreen ? 'build-a-box__message--no-postage' : ''}">
                <div class="build-a-box__message-content js-toggle-build-a-box__message-content">
                    <div class="build-a-box__message-content-title-container">
                        <div class="build-a-box__message-icon">
                            ${isGreen ? `
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M10.6 16.2L17.65 9.15L16.25 7.75L10.6 13.4L7.75 10.55L6.35 11.95L10.6 16.2ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5Z" fill="#18303F"/>
                                </svg>
                            ` : `
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 7C11.7167 7 11.4792 7.09583 11.2875 7.2875C11.0958 7.47917 11 7.71667 11 8C11 8.28333 11.0958 8.52083 11.2875 8.7125C11.4792 8.90417 11.7167 9 12 9C12.2833 9 12.5208 8.90417 12.7125 8.7125C12.9042 8.52083 13 8.28333 13 8C13 7.71667 12.9042 7.47917 12.7125 7.2875C12.5208 7.09583 12.2833 7 12 7ZM13 11H11V17H13V11ZM12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2Z" fill="#EF578A"/>
                                </svg>
                            `}
                        </div>
                        <p class="build-a-box__message-content-title">${messageTitle}</p>
                        <span class="build-a-box__message-content-icon--arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.5303 9.46967C16.8232 9.76256 16.8232 10.2374 16.5303 10.5303L12.5303 14.5303C12.2374 14.8232 11.7626 14.8232 11.4697 14.5303L7.46967 10.5303C7.17678 10.2374 7.17678 9.76256 7.46967 9.46967C7.76256 9.17678 8.23744 9.17678 8.53033 9.46967L12 12.9393L15.4697 9.46967C15.7626 9.17678 16.2374 9.17678 16.5303 9.46967Z" fill="#18303F"/>
                            </svg>
                        </span>
                    </div>
                    <p class="build-a-box__message-content-text js-content-toggle-build-a-box__message-content-text">${messageText}</p>
                </div>
            </div>
        `;
    }

    renderTabs() {
        const types = this.productData.product_types || [];
        const preferredOrder = (this.state.general_types_tags || []);
        const orderedTypes = preferredOrder.length
            ? [...preferredOrder.filter(t => types.includes(t)), ...types.filter(t => !preferredOrder.includes(t))]
            : types;
        const allCount = this.productData.products.length;
        
        const tabsHTML = orderedTypes.map(type => {
            const count = this.productData.products.filter(p => p.type === type).length;
            const isActive = this.state.current_filter === type ? 'build-a-box__tab--active' : '';
            const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
            
            return `
                <button 
                    class="build-a-box__tab ${isActive}" 
                    data-filter="${type}"
                >
                    ${typeLabel} (${count})
                </button>
            `;
        }).join('');

        const allActiveClass = this.state.current_filter === 'all' ? 'build-a-box__tab--active' : '';

        return `
            <div class="build-a-box__tabs">
                <button class="build-a-box__tab ${allActiveClass}" data-filter="all">
                    ${this.getTranslation('all_tab')} (${allCount})
                </button>
                ${tabsHTML}
            </div>
        `;
    }

    renderProductGrid() {
        const filteredProducts = this.state.current_filter === 'all' 
            ? this.productData.products 
            : this.productData.products.filter(product => product.type === this.state.current_filter);

        const container = document.createElement('div');
        container.className = 'build-a-box__products-grid';
        
        const template = document.getElementById('build-a-box-product-card-template');
        if (!template) {
            console.warn('[BuildABox] Product card template not found');
            return container.outerHTML;
        }

        filteredProducts.forEach(product => {
            const productCard = this.createProductCardFromTemplate(template, product);
            container.appendChild(productCard);
        });

        return container.outerHTML;
    }

    createProductCardFromTemplate(template, product) {
        const selectedProduct = this.state.selected_products.find(p => p.id === product.id);
        const quantity = selectedProduct ? selectedProduct.quantity : 0;
        const cardBackground = product.metafields?.custom?.card_background || '';

        const productCard = template.content.cloneNode(true);
        
        // Populate product data
        const cardElement = productCard.querySelector('.build-a-box__product-card');
        const image = productCard.querySelector('.build-a-box__product-image');
        const imageLink = productCard.querySelector('.build-a-box__product-image-link');
        const title = productCard.querySelector('.build-a-box__product-title');
        const titleLink = productCard.querySelector('.build-a-box__product-title-link');
        const description = productCard.querySelector('.build-a-box__product-description');
        const price = productCard.querySelector('.build-a-box__product-price');
        const quantityDisplay = productCard.querySelector('.build-a-box__quantity-selector-display');
        const minusBtn = productCard.querySelector('.build-a-box__quantity-btn--minus');
        const plusBtn = productCard.querySelector('.build-a-box__quantity-btn--plus');
        const ingredientsBtn = productCard.querySelector('.build-a-box__ingredients-btn');
        const ingredientsList = productCard.querySelector('.build-a-box__ingredients-list');

        if (cardElement) {
            cardElement.setAttribute('data-product-id', product.id);
            cardElement.setAttribute('data-product-type', product.type || '');
            
            // Set card background color from metafield dynamically like in quiz
            const bgColor = cardBackground || '#EEFFE9';
            cardElement.style.setProperty('--build-a-box-card-background', bgColor);
            if (cardBackground) {
                cardElement.setAttribute('data-card-background', cardBackground);
            }
        }

        if (image && product.featured_image) {
            image.src = product.featured_image.url + '&width=836';
            image.alt = product.featured_image.alt || product.title;
        }

        if (title) {
            title.textContent = product.title;
        }

        if (imageLink && product.url) {
            imageLink.href = product.url;
        }

        if (titleLink && product.url) {
            titleLink.href = product.url;
        }

        if (description) {
            const decodedDescription = this.decodeHtmlEntities(product.description || '');
            description.textContent = decodedDescription;
        }
        
        if (price) {
            const priceFormatted = this.formatPrice(product.price || 0);
            price.textContent = priceFormatted;
        }

        if (ingredientsList) {
           ingredientsList.textContent = this.decodeHtmlEntities(product.description || '');
        }

        if (quantityDisplay) {
            quantityDisplay.value = quantity;
            quantityDisplay.setAttribute('data-product-id', product.id);
            
            // Set max based on inventory if available
            const maxQuantity = product.first_available_variant?.inventory_quantity || 999;
            if (maxQuantity && maxQuantity > 0) {
                quantityDisplay.setAttribute('max', maxQuantity);
            }
        }

        if (minusBtn) {
            minusBtn.disabled = quantity <= 0;
            minusBtn.classList.toggle('build-a-box__quantity-btn--disabled', quantity <= 0);
            minusBtn.setAttribute('data-action', 'decrease');
            minusBtn.setAttribute('data-product-id', product.id);
        }

        if (plusBtn) {
            plusBtn.setAttribute('data-action', 'increase');
            plusBtn.setAttribute('data-product-id', product.id);
            
            // Disable plus button if at max quantity
            const maxQuantity = product.first_available_variant?.inventory_quantity;
            if (maxQuantity && maxQuantity > 0 && quantity >= maxQuantity) {
                plusBtn.disabled = true;
                plusBtn.classList.add('build-a-box__quantity-btn--disabled');
            }
        }
        
        // Mark card as selected if it has quantity
        if (cardElement && quantity > 0) {
            cardElement.classList.add('build-a-box__product-card--selected');
        }

        // Setup ingredients functionality text
        const ingredientsShowText = productCard.querySelector('.build-a-box__ingredients-text');
        const ingredientsCloseText = productCard.querySelector('.build-a-box__ingredients-close-text');
        const ingredientsTitle = productCard.querySelector('.build-a-box__ingredients-title');
        
        if (ingredientsShowText) {
            ingredientsShowText.textContent = this.getTranslation('ingredients_show');
        }
        if (ingredientsCloseText) {
            ingredientsCloseText.textContent = this.getTranslation('ingredients_close');
        }
        if (ingredientsTitle) {
            ingredientsTitle.textContent = this.getTranslation('ingredients_title');
        }

        return productCard.firstElementChild;
    }

    renderSidebarHeader() {
        const isMinimumMet = this.stateManager.isMinimumMet();
        const isVisible = this.state.sidebar_visible || this.state.total_items > 0;
        const visibilityClass = isVisible ? 'build-a-box__sidebar-header--visible' : '';
        const productsOpenClass = this.state.sidebar_products_open ? 'build-a-box__sidebar-products--open' : '';

        return `
          
            <div class="build-a-box__sidebar-header ${visibilityClass} ${this.state.sidebar_products_open ? 'build-a-box__sidebar-header--open' : ''}">
                <div class="build-a-box__sidebar-title">
                    <h3>${this.getTranslation('choose_your_recipes')}</h3>
                    <p class="build-a-box__sidebar-subtitle">${this.getTranslation('minimum_8_forage_original')}</p>
                </div>
                
                <div class="build-a-box__sidebar-summary">
                    <div class="build-a-box__sidebar-count">${this.state.total_items} ${this.getTranslation('items')}</div>
                    <button class="build-a-box__sidebar-toggle" data-action="toggle-sidebar">
                        ${this.state.sidebar_products_open ? this.getTranslation('close_choices') : this.getTranslation('see_choices')}
                    </button>
                </div>
                
                <div class="build-a-box__sidebar-products ${productsOpenClass}">
                    ${this.renderSelectedProducts()}
                </div>
                
                <button 
                    class="build-a-box__delivery-open" 
                    data-action="open-delivery-bottom-sheet"
                    ${!isMinimumMet || this.state.total_items === 0 ? 'disabled' : ''}
                >
                    ${!isMinimumMet || this.state.total_items === 0 
                        ? this.getTranslation('add_8_meals')
                        : this.getTranslation('select_delivery_options')
                    }
                </button>
            </div>
        `;
    }

    renderSidebar() {
        const isVisible = this.state.sidebar_visible;
        const visibilityClass = isVisible ? 'build-a-box__sidebar--visible' : '';
        const deliveryModalClass = (isVisible && this.state.delivery_modal_open) ? 'build-a-box__sidebar--delivery-modal' : '';
        const showOverlay = this.state.sidebar_visible;

        return `
            <div class="build-a-box__message-container small-hide">${this.renderMessage()}</div>
            ${this.renderSidebarHeader()}
            <div class="build-a-box__sidebar ${visibilityClass} ${deliveryModalClass}">${this.renderSidebarContent()}</div>
            ${showOverlay ? '<div class="build-a-box__sidebar-overlay" data-action="close-sidebar"></div>' : ''}
        `;
    }

    renderSidebarContent() {
        const subtotalFormatted = this.formatPrice(this.state.subtotal || 0);
        const discountFormatted = this.formatPrice(this.state.discount_amount || 0);
        const totalFormatted = this.formatPrice(this.state.final_total || 0);
        const isMinimumMet = this.stateManager.isMinimumMet();
        const showHandle = this.state.delivery_modal_open;
        const sellingPlans = Array.isArray(this.productData?.selling_plans) ? this.productData.selling_plans : [];
        const showPlanSelect = sellingPlans.length > 0;
        const currentFilter = this.state.current_filter?.toLowerCase();
        return `
            <div class="build-a-box__delivery-inline">
              ${showHandle ? '<div class="build-a-box__sidebar-handle"></div>' : ''}
            <div class="build-a-box__delivery-options">
                <h4>${this.getTranslation('choose_delivery')}</h4>
                
                <div class="build-a-box__delivery-option build-a-box__delivery-option--subscription">
                    <div class="build-a-box__delivery-option-content">
                        <span>${this.getTranslation('subscribe_save')}</span>
                        <span class="build-a-box__discount">${this.getTranslation('save_percent', { percent: this.state.subscription_discount })}</span>
                    </div>
                    <input 
                        type="radio" 
                        name="delivery-type" 
                        value="subscription" 
                        ${this.state.delivery_type === 'subscription' ? 'checked' : ''}
                    >
                    
                    ${this.renderSubscriptionBenefits()}

                     ${showPlanSelect ? `
                <div class="build-a-box__delivery-frequency">
                  <label for="delivery-frequency-select">${this.getTranslation('delivered')}</label>
                  <select name="delivery-frequency" id="delivery-frequency-select" class="build-a-box__delivery-select">
                    ${sellingPlans.map(sp => `
                      <option value="${sp.id}" ${String(sp.id) === String(this.state.selected_selling_plan_id) ? 'selected' : ''}>${sp.name}</option>
                    `).join('')}
                  </select>
                </div>
                ` : ''}
                </div>
                
               
                
                <div class="build-a-box__delivery-option build-a-box__delivery-option--onetime">
                    <div class="build-a-box__delivery-option-content">
                        <span>${this.getTranslation('one_time_purchase')}</span>
                    </div>
                    <input 
                        type="radio" 
                        name="delivery-type" 
                        value="onetime" 
                        ${this.state.delivery_type === 'onetime' ? 'checked' : ''}
                    >
                </div>


            </div>
            
            <div class="build-a-box__pricing">
                <div class="build-a-box__pricing-line">
                    <span>${this.getTranslation('subtotal')}</span>
                    <span>${subtotalFormatted}</span>
                </div>
                
                ${this.state.delivery_type === 'subscription' && this.state.discount_amount > 0 ? `
                    <div class="build-a-box__pricing-line build-a-box__pricing-discount">
                        <span>${this.getTranslation('discount', { percent: this.state.subscription_discount })}</span>
                        <span>-${discountFormatted}</span>
                    </div>
                ` : ''}
                
                <div class="build-a-box__pricing-line build-a-box__pricing-total">
                    <span>${this.getTranslation('total')}</span>
                    <span>${totalFormatted}</span>
                </div>
            </div>
            
            ${(currentFilter === 'treats' || currentFilter === 'boosts') ? `
                <button class="build-a-box__sidebar-toggle skip-reminder-button" data-action="skip-reminder">
                    ${currentFilter === 'treats' ? this.getTranslation('skip') : this.getTranslation('skip_and_add_to_basket')}
                </button>
            ` : ''}
            
            <button 
                class="build-a-box__add-to-cart" 
                ${!isMinimumMet || this.state.total_items === 0 ? 'disabled' : ''}
                data-action="add-to-cart"
            >
                ${this.getTranslation('add_to_basket')}
            </button>
            
            <div class="build-a-box__checkout-info">
                <div class="build-a-box__checkout-info-item">
                    <div class="build-a-box__checkout-info-icon"></div>
                    <p class="build-a-box__checkout-info-text">${this.getTranslation('checkout_info')}</p>
                </div>
                <div class="build-a-box__checkout-info-item">
                    <div class="build-a-box__checkout-info-icon"></div>
                    <p class="build-a-box__checkout-info-text">${this.getTranslation('pick_preferred_date')}</p>
                </div>
            </div>
            </div>
        `;
    }

    renderSelectedProducts() {
        if (this.state.selected_products.length === 0) {
            return '';
        }

        const productsHTML = this.state.selected_products.map(product => {
            return `
                <div class="build-a-box__sidebar-product" data-product-id="${product.id}">
                    <div class="build-a-box__sidebar-product-name">${product.title}</div>
                    <div class="build-a-box__sidebar-product-selector">
                        <button class="build-a-box__sidebar-quantity-btn build-a-box__sidebar-quantity-btn--minus" data-action="decrease" data-product-id="${product.id}">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <g opacity="1">
                                <circle cx="8" cy="8" r="8" fill="white"/>
                                <path d="M5 8L11 8" stroke="#18303F" stroke-linecap="round"/>
                            </g> 
                            </svg>
                        </button>
                        <div class="build-a-box__sidebar-quantity-badge">
                            ${product.quantity}
                        </div>
                        <button class="build-a-box__sidebar-quantity-btn build-a-box__sidebar-quantity-btn--plus" data-action="increase" data-product-id="${product.id}">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3.33337V12.6667M3.33337 8H12.6667" stroke="#18303F" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            `;
        }).join('');

        return productsHTML;
    }

    bindEvents() {
        this.initBottomSheet();
        
        this.addEventListener('click', (e) => {
            const action = e.target.dataset.action || e.target.closest('[data-action]')?.dataset.action;
            
            if (!action) {
                const title = e.target.closest('.build-a-box__message-content-title-container');
                if (title) {
                    const wrapper = title.closest('.js-toggle-build-a-box__message-content');
                    this.toggleMessageDropdown(wrapper);
                    return;
                }

                // Handle ingredients button clicks
                if (e.target.matches('.build-a-box__ingredients-btn') || e.target.closest('.build-a-box__ingredients-btn')) {
                    const card = e.target.closest('.build-a-box__product-card');
                    if (card) {
                        const productId = card.dataset.productId;
                        const product = this.productData.products.find(p => p.id.toString() === productId);
                        this.handleIngredientsShow(card, product);
                    }
                    return;
                }
                
                // Handle ingredients close button clicks
                if (e.target.matches('.build-a-box__ingredients-close') || e.target.closest('.build-a-box__ingredients-close')) {
                    const card = e.target.closest('.build-a-box__product-card');
                    if (card) {
                        this.handleIngredientsClose(card);
                    }
                    return;
                }
                
                // Handle tab clicks
                if (e.target.matches('.build-a-box__tab')) {
                    this.handleTabChange(e.target.dataset.filter);
                    return;
                }
                 
                // Handle click on entire delivery option block
                const deliveryOption = e.target.closest('.build-a-box__delivery-option');
                if (deliveryOption) {
                    if (e.target.closest('select[name="delivery-frequency"]')) return;
                    const radio = deliveryOption.querySelector('input[type="radio"][name="delivery-type"]');
                    if (radio) {
                        if (!radio.checked) radio.checked = true;
                        this.handleDeliveryTypeChange(radio.value);
                    }
                    return;
                }
                
                return;
            }

            switch (action) {
                case 'increase':
                    this.handleQuantityChange(e.target, 1);
                    break;
                case 'decrease':
                    this.handleQuantityChange(e.target, -1);
                    break;
                case 'toggle-sidebar':
                    this.toggleSidebar();
                    break;
                case 'remove-product':
                    this.handleRemoveProduct(e.target);
                    break;
                case 'add-to-cart':
                    this.handleAddToCart();
                    break;
                case 'open-delivery-bottom-sheet':
                    this.openDeliveryBottomSheet();
                    break;
                case 'close-sidebar':
                    this.closeSidebar();
                    break;
                case 'skip-reminder':
                    this.handleSkipReminder();
                    break;
            }
        });

        this.addEventListener('change', (e) => {
            if (e.target.name === 'delivery-type') {
                this.handleDeliveryTypeChange(e.target.value);
            }
            if (e.target.name === 'delivery-frequency') {
                this.handleFrequencyChange(e.target.value);
            }
        });
        
        // Handle input field changes for quantity selectors
        this.addEventListener('input', (e) => {
            if (e.target.classList.contains('build-a-box__quantity-selector-display')) {
                this.handleQuantityInput(e.target);
            }
        });
        
        // Handle blur event to enforce max value
        this.addEventListener('blur', (e) => {
            if (e.target.classList.contains('build-a-box__quantity-selector-display')) {
                this.handleQuantityBlur(e.target);
            }
        }, true);
        
        // Prevent non-numeric input
        this.addEventListener('keydown', (e) => {
            if (e.target.classList.contains('build-a-box__quantity-selector-display')) {
                // Allow: backspace(8), tab(9), escape(27), enter(13), delete(46),
                // home(36), end(35), left(37), up(38), right(39), down(40), page up(33), page down(34)
                if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40, 33, 34].indexOf(e.keyCode) !== -1 ||
                    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true)) {
                    return;
                }
                // Ensure that it is a number and stop the keypress
                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                }
            }
        });
    }

    handleQuantityChange(target, change) {
        // Check if it's from product card or sidebar
        const productCard = target.closest('.build-a-box__product-card');
        const sidebarProduct = target.closest('.build-a-box__sidebar-product');
        
        let productId;
        
        if (productCard) {
            productId = parseInt(productCard.dataset.productId);
        } else if (sidebarProduct) {
            productId = parseInt(target.dataset.productId || sidebarProduct.dataset.productId);
        } else {
            return;
        }

        const product = this.productData.products.find(p => p.id === productId);
        if (!product) return;

        if (change > 0) {
            // Check availability before adding
            const currentQuantity = this.state.selected_products.find(p => p.id === productId)?.quantity || 0;
            const maxQuantity = product.first_available_variant?.inventory_quantity || 999;
            
            if (maxQuantity && currentQuantity + change > maxQuantity) {
                // Can't add more than available
                const availableToAdd = maxQuantity - currentQuantity;
                if (availableToAdd > 0) {
                    this.stateManager.addProduct(product, availableToAdd);
                }
            } else {
                this.stateManager.addProduct(product, change);
            }
        } else {
            this.stateManager.removeProduct(productId, Math.abs(change));
        }
    }
    
    handleQuantityInput(input) {
        const productCard = input.closest('.build-a-box__product-card');
        if (!productCard) return;
        
        const productId = parseInt(productCard.dataset.productId);
        const product = this.productData.products.find(p => p.id === productId);
        if (!product) return;
        
        let newValue = parseInt(input.value) || 0;
        
        // Prevent negative values
        if (newValue < 0) {
            newValue = 0;
            input.value = 0;
        }
        
        // Check max value while typing
        const maxQuantity = parseInt(input.getAttribute('max')) || 999;
        if (newValue > maxQuantity) {
            newValue = maxQuantity;
            input.value = maxQuantity;
        }
        
        const currentQuantity = this.state.selected_products.find(p => p.id === productId)?.quantity || 0;
        const difference = newValue - currentQuantity;
        
        if (difference !== 0) {
            // Update the state based on the new value
            if (difference > 0) {
                this.stateManager.addProduct(product, difference);
            } else {
                this.stateManager.removeProduct(productId, Math.abs(difference));
            }
        }
    }
    
    handleQuantityBlur(input) {
        const productCard = input.closest('.build-a-box__product-card');
        if (!productCard) return;
        
        const productId = parseInt(productCard.dataset.productId);
        const product = this.productData.products.find(p => p.id === productId);
        if (!product) return;
        
        let value = parseInt(input.value) || 0;
        
        // Ensure value is within bounds on blur
        const maxQuantity = parseInt(input.getAttribute('max')) || 999;
        if (value > maxQuantity) {
            value = maxQuantity;
            input.value = maxQuantity;
            
            // Update state to match the corrected value
            const currentQuantity = this.state.selected_products.find(p => p.id === productId)?.quantity || 0;
            const difference = value - currentQuantity;
            
            if (difference !== 0) {
                if (difference > 0) {
                    this.stateManager.addProduct(product, difference);
                } else {
                    this.stateManager.removeProduct(productId, Math.abs(difference));
                }
            }
        }
        
        // Ensure minimum value
        if (value < 0) {
            input.value = 0;
        }
    }

    toggleSidebar() {
        const shouldOpen = !this.state.sidebar_products_open;
        this.animateSidebarHeaderToggle(shouldOpen);
    }

    handleRemoveProduct(target) {
        const productId = parseInt(target.dataset.productId);
        if (productId) {
            // Remove all quantity of this product
            const product = this.state.selected_products.find(p => p.id === productId);
            if (product) {
                this.stateManager.removeProduct(productId, product.quantity);
            }
        }
    }

    handleTabChange(filter) {
        this.stateManager.updateField('current_filter', filter);
        
        this.querySelectorAll('.build-a-box__tab').forEach(tab => {
            tab.classList.remove('build-a-box__tab--active');
        });
        
        const targetTab = this.querySelector(`[data-filter="${filter}"]`);
        if (targetTab) {
            targetTab.classList.add('build-a-box__tab--active');
        }
        
        this.updateProductGrid();
        
        const filterLower = filter.toLowerCase();
        if (filterLower !== 'treats' && filterLower !== 'boosts') {
            this.restoreOriginalTitle();
        }
    }

    handleDeliveryTypeChange(type) {
        if (type === 'subscription') {
            const currentPlanId = this.state.selected_selling_plan_id;
            const fallbackPlanId = this.productData?.selected_selling_plan?.id || this.productData?.selling_plans?.[0]?.id || null;
            const planIdToUse = currentPlanId || fallbackPlanId;
            const discount = planIdToUse ? this.getDiscountPercentForPlanId(planIdToUse) : (this.state.subscription_discount || 5);
            this.stateManager.updateState({
                delivery_type: type,
                subscription_discount: discount,
                selected_selling_plan_id: planIdToUse
            });
            if (planIdToUse) this.dynamicPricing.updateSelectedSellingPlan(planIdToUse);
        } else {
            // One-time: keep previously selected plan and discount for quick return to subscription
            this.stateManager.updateState({
                delivery_type: type
            });
        }
        this.stateManager.recalculateTotals();
        if (this.state.delivery_modal_open) this.initTouchHandlers();
    }

    handleFrequencyChange(planIdOrFrequency) {
        const sellingPlans = Array.isArray(this.productData?.selling_plans) ? this.productData.selling_plans : [];

        if (sellingPlans.length > 0) {
            const discount = this.getDiscountPercentForPlanId(planIdOrFrequency) ?? this.state.subscription_discount;
            this.stateManager.updateState({ 
                selected_selling_plan_id: planIdOrFrequency, 
                subscription_discount: discount,
                subscription_frequency: planIdOrFrequency 
            });
            this.dynamicPricing.updateSelectedSellingPlan(planIdOrFrequency);
        } else {
            this.stateManager.updateState({ 
                subscription_frequency: planIdOrFrequency,
                subscription_discount: this.state.subscription_discount || 5
            });
        }
        this.stateManager.recalculateTotals();
        if (this.state.delivery_modal_open) this.initTouchHandlers();
    }

    getDiscountPercentForPlanId(planId) {
        const plans = Array.isArray(this.productData?.selling_plans) ? this.productData.selling_plans : [];
        const plan = plans.find(p => String(p.id) === String(planId));
        if (!plan) return 5;
        
        const adjustments = Array.isArray(plan.price_adjustments) ? plan.price_adjustments : [];
        for (const adj of adjustments) {
            if (adj && (adj.value_type === 'percentage' || adj.adjustment_type === 'percentage')) {
                if (typeof adj.value === 'number') return adj.value;
                if (adj.value && typeof adj.value.percentage === 'number') return adj.value.percentage;
            }
        }
        
        const match = typeof plan.name === 'string' ? plan.name.match(/(\d+)%/) : null;
        if (match) return parseInt(match[1], 10);
        
        return 5;
    }

    handleIngredientsShow(card, product) {
        if (!card || !product) return;
        
        const overlay = card.querySelector('.build-a-box__ingredients-overlay');
        const ingredientsList = overlay?.querySelector('.build-a-box__ingredients-list');
        
        if (overlay && ingredientsList)  {
            const ingredients = product.ingredients || product.metafields?.ingredients || '';
            const ingredientsText = Array.isArray(ingredients) ? ingredients.join(', ') : ingredients;
            
            ingredientsList.textContent = this.decodeHtmlEntities(ingredientsText || '');
            overlay.classList.add('build-a-box__ingredients-overlay--active');
        }
    }

    handleIngredientsClose(card) {
        if (!card) return;
        const overlay = card.querySelector('.build-a-box__ingredients-overlay');
        if (overlay) {
            overlay.classList.remove('build-a-box__ingredients-overlay--active');
        }
    }

    hasProductsOfType(type) {
        return this.state.selected_products.some(product => {
            const productType = product.type?.toLowerCase() || '';
            return productType === type.toLowerCase();
        });
    }

    scrollToMain() {
        const mainElement = this.querySelector('.build-a-box__main');
        if (mainElement) {
            const header = document.querySelector('header');
            const headerHeight = header ? header.offsetHeight : 100;
            const offset = headerHeight + 50;
            const elementPosition = mainElement.getBoundingClientRect().top;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetPosition = scrollTop + elementPosition - offset;
            
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    }

    showReminderForType(type) {
        const messageKey = type.toLowerCase() === 'treats' ? 'reminder_add_treats' : 'reminder_add_boosts';
        const reminderText = this.getTranslation(messageKey);
        const decodedText = this.decodeHtmlEntities(reminderText);
        
        const titleElement = this.querySelector('.build-a-box__container-title-text');
        if (titleElement) {
            titleElement.textContent = decodedText;
        }
        
        this.classList.add('build-a-box--reminder-active');
        
        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        this.stateManager.updateField('current_filter', capitalizedType);
        
        this.updateProductGrid();
        this.updateSidebar();
        
        this.scrollToMain();
    }
 
    restoreOriginalTitle() {
        const titleElement = this.querySelector('.build-a-box__container-title-text');
        if (titleElement) {
            const originalText = this.getTranslation('container_title');
            const decodedText = this.decodeHtmlEntities(originalText);
            titleElement.textContent = decodedText;
        }
        
        this.classList.remove('build-a-box--reminder-active');
        
        this.scrollToMain();
    }

    restoreCompleteState() {
        this.restoreOriginalTitle();
        
        this.stateManager.updateField('current_filter', 'all');
        this.querySelectorAll('.build-a-box__tab').forEach(tab => {
            tab.classList.remove('build-a-box__tab--active');
        });
        const allTab = this.querySelector('[data-filter="all"]');
        if (allTab) {
            allTab.classList.add('build-a-box__tab--active');
        }
        this.updateProductGrid();
        this.updateSidebar();
    }

    checkRemindersBeforeCart() {
        if (!this.hasProductsOfType('treats') && !this.state.reminder_state.treats_shown) {
            this.showReminderForType('treats');
            this.stateManager.updateState({
                reminder_state: { ...this.state.reminder_state, treats_shown: true }
            });
            return false;
        }

        if (!this.hasProductsOfType('boosts') && !this.state.reminder_state.boosts_shown) {
            this.showReminderForType('boosts');
            this.stateManager.updateState({
                reminder_state: { ...this.state.reminder_state, boosts_shown: true }
            });
            return false;
        }

        return true;
    }

    handleSkipReminder() {
        const currentFilter = this.state.current_filter?.toLowerCase();
        
        if (currentFilter === 'treats') {
            this.stateManager.updateState({
                reminder_state: { ...this.state.reminder_state, treats_shown: true }
            });
        } else if (currentFilter === 'boosts') {
            this.stateManager.updateState({
                reminder_state: { ...this.state.reminder_state, boosts_shown: true }
            });
        }
        
        // Restore the original state and continue with the cart process
        this.restoreCompleteState();
        
        // Continue with the cart process
        this.handleAddToCart();
    }

    async handleAddToCart() {
        const isMinimumMet = this.stateManager.isMinimumMet();
        if (!isMinimumMet || this.state.total_items === 0) {
            console.warn('Cannot add to cart: minimum requirements not met');
            return;
        }

        if (!this.isMobile()) {
            if (!this.checkRemindersBeforeCart()) {
                return;
            }
        }

        const button = this.querySelector('.build-a-box__add-to-cart');
        button.disabled = true;
        button.textContent = 'Adding...';

        try {
            if (this.state.delivery_type === 'subscription') {
                await this.createRechargeBundle();
            } else {
                await this.addToCartOneTime();
            }
            await this.updateAllCartContent();
            if (window.vennApps?.isAppView) {
                // In app: native cart updated via bridge, skip opening web cart drawer
            } else if (window.location.pathname !== '/cart') {
                await this.openAjaxCart();
            }
            
            this.restoreCompleteState();
            
            this.stateManager.updateState({
                reminder_state: { treats_shown: false, boosts_shown: false }
            });
             
        } catch (error) {
            console.error('Error adding to cart:', error);
        } finally {
            button.disabled = false;
            button.textContent = this.getTranslation('add_to_basket');
        }
    }

    async createRechargeBundle() {
        const session = {
            selected_products: this.state.selected_products.map(p => ({
                id: p.id,
                product_id: p.id,
                variant_id: p.first_available_variant?.id || p.id,
                quantity: p.quantity,
                title: p.title
            }))
        };
        const items = await this.dynamicPricing.createRechargeBundle(session, this.state.selected_selling_plan_id);
        const variantToProduct = {};
        (session.selected_products || []).forEach(p => {
            const vid = p.variant_id || p.first_available_variant?.id;
            if (vid) variantToProduct[vid] = p.product_id || p.id;
        });
        const itemsWithProductId = items.map(item => ({
            ...item,
            product_id: item.product_id || variantToProduct[item.id]
        }));
        if (window.VennAppsCartBridge) {
            window.VennAppsCartBridge.postAddCartLines(itemsWithProductId);
        }
        const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(base + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        });
        if (!res.ok) {
            const err = await res.text();
            throw new Error(`Failed to add bundle to cart: ${err}`);
        }
        return await res.json();
    }

    #getShippingRulesProperties(product) {
        const PRODUCT_TAGS = {
            ORIGINAL: ['Original', 'original'],
            FORAGE: ['Forage', 'forage'],
            ORIGINAL_BOX: 'original_box',
            FORAGE_BOX: 'forage-box',
            TESTER: 'Taster-box',
            STARTER: 'starter'
        };

        const properties = {};
        
        if (!product.tags) return properties;
        
        const tags = Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()) : []);
        
        let shippingType = '';
        
        if (tags.some(tag => PRODUCT_TAGS.ORIGINAL.includes(tag))) {
            shippingType = 'Original';
        } else if (tags.some(tag => PRODUCT_TAGS.FORAGE.includes(tag))) {
            shippingType = 'Forage';
        } else if (tags.includes(PRODUCT_TAGS.ORIGINAL_BOX)) {
            shippingType = 'original_box';
        } else if (tags.includes(PRODUCT_TAGS.FORAGE_BOX)) {
            shippingType = 'forage-box';
        } else if (tags.includes(PRODUCT_TAGS.TESTER)) {
            shippingType = 'Taster-box';
        } else if (tags.includes(PRODUCT_TAGS.STARTER)) {
            shippingType = 'starter';
        }
        
        if (shippingType) {
            properties._shipping_rules_type_product = shippingType;
            if (product.metafields?.custom?.number_of_items_in_the_box) {
                properties._shipping_rules_box_number = product.metafields.custom.number_of_items_in_the_box;
            }
        }
        
        return properties;
    }

    async addToCartOneTime() {
        const cartItems = this.state.selected_products.map(product => {
            const baseProperties = {
                _bundle_type: 'build_a_box',
                _bundle_id: this.state.box_id,
                _product_type: product.type,
                _bundle_title: product.title
            };
            
            const shippingProperties = this.#getShippingRulesProperties(product);
            
            return {
                id: product.first_available_variant?.id || product.id,
                product_id: product.id,
                quantity: product.quantity,
                properties: { ...baseProperties, ...shippingProperties }
            };
        });

        if (window.VennAppsCartBridge) {
            window.VennAppsCartBridge.postAddCartLines(cartItems);
        }

        const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(base + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: cartItems })
        });

        if (!response.ok) {
            throw new Error('Failed to add items to cart');
        }

        return await response.json();
    }

    async updateAllCartContent() {
        try {
            const sectionsToUpdate = [
                { id: 'main-cart-items', section: 'main-cart-items', selector: '.shopify-section' },
                { id: 'main-cart-footer', section: 'main-cart-footer', selector: '.shopify-section' },
                { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
            ];

            const cartResponse = await fetch(`${routes.cart_url}.js`);
            const cartData = await cartResponse.json();

            const sectionIds = sectionsToUpdate.map(s => s.section).join(',');
            const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const url = `${base}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(window.location.pathname)}`;

            const sectionsResponse = await fetch(url, { credentials: 'same-origin' });
            if (!sectionsResponse.ok) {
                throw new Error('Failed to fetch sections');
            }
            const sections = await sectionsResponse.json();

            sectionsToUpdate.forEach(section => {
                const targetElement = document.getElementById(section.id);
                if (targetElement && sections[section.section]) {
                    const parser = new DOMParser();
                    const html = parser.parseFromString(sections[section.section], 'text/html');
                    const sourceElement = html.querySelector(section.selector);
                    if (sourceElement) {
                        targetElement.innerHTML = sourceElement.innerHTML;
                    }
                }
            });

            const cartIcons = document.querySelectorAll('.header__icon--cart');
            if (sections['cart-icon-bubble']) {
                const parser = new DOMParser();
                const html = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
                const sourceIcon = html.querySelector('.shopify-section');
                if (sourceIcon) {
                    cartIcons.forEach(icon => {
                        icon.innerHTML = sourceIcon.innerHTML;
                    });
                }
            }

            const cartItems = document.querySelector('cart-items');
            const cartFooter = document.getElementById('main-cart-footer');
            const cartDrawer = document.querySelector('cart-drawer');
            const isEmpty = cartData.item_count === 0;
            if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
            if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
            if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
        } catch (error) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems && cartItems.onCartUpdate) {
                await cartItems.onCartUpdate();
            }
        }
    }

    async openAjaxCart() {
        try {
            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer) {
                return;
            }
            cartDrawer.classList.remove('is-empty');
            const sectionIds = cartDrawer
                .getSectionsToRender()
                .map((s) => s.id)
                .join(',');
            const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const url = `${base}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(window.location.pathname)}`;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) return;
            const sections = await res.json();
            cartDrawer.renderContents({ sections });
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-a-box' });
            }
        } catch (e) {
        }
    }

    updateUI(changes) {
        if (changes.hasOwnProperty('current_filter')) {
            this.updateProductGrid();
        }
        
        if (changes.hasOwnProperty('delivery_type') || changes.hasOwnProperty('subscription_discount')) {
            this.updateSidebar();
        }
        
        if (changes.hasOwnProperty('sidebar_visible')) {
            this.updateSidebar();
        }
    }

    updateProductCards() {
        const productCards = this.querySelectorAll('.build-a-box__product-card');
        productCards.forEach(card => {
            const productId = parseInt(card.dataset.productId);
            const selectedProduct = this.state.selected_products.find(p => p.id === productId);
            const quantity = selectedProduct ? selectedProduct.quantity : 0;
            
            const quantityDisplay = card.querySelector('.build-a-box__quantity-selector-display');
            const minusBtn = card.querySelector('.build-a-box__quantity-btn--minus');
            const plusBtn = card.querySelector('.build-a-box__quantity-btn--plus');
            
            if (quantityDisplay) {
                quantityDisplay.value = quantity;
                
                // Update max based on product inventory
                const product = this.productData.products.find(p => p.id === productId);
                if (product) {
                    const maxQuantity = product.first_available_variant?.inventory_quantity || 999;
                    if (maxQuantity && maxQuantity > 0) {
                        quantityDisplay.setAttribute('max', maxQuantity);
                    }
                }
            }
            
            if (minusBtn) {
                minusBtn.disabled = quantity <= 0;
                minusBtn.classList.toggle('build-a-box__quantity-btn--disabled', quantity <= 0);
            }
            
            if (plusBtn) {
                // Check if at max quantity
                const product = this.productData.products.find(p => p.id === productId);
                const maxQuantity = product?.first_available_variant?.inventory_quantity;
                const atMax = maxQuantity && maxQuantity > 0 && quantity >= maxQuantity;
                plusBtn.disabled = atMax;
                plusBtn.classList.toggle('build-a-box__quantity-btn--disabled', atMax);
            }
            
            // Update card selection state
            card.classList.toggle('build-a-box__product-card--selected', quantity > 0);
        });
    }

    updateMessage() {
        const isReminderActive = this.classList.contains('build-a-box--reminder-active');
        const currentTitle = isReminderActive ? this.querySelector('.build-a-box__container-title-text')?.textContent : null;
        
        // Update the mobile message (with title)
        this.renderToContainer('.build-a-box__message-container.large-up-hide', this.renderMessageWithTitle());
        
        // Update the sidebar (which includes the desktop message)
        this.renderToContainer('.build-a-box__sidebar-container', this.renderSidebar());
        
        if (isReminderActive && currentTitle) {
            const titleElements = this.querySelectorAll('.build-a-box__container-title-text');
            titleElements.forEach(titleElement => {
                titleElement.textContent = currentTitle;
            });
        }
    } 

    updateProductGrid() {
        const container = this.querySelector('.build-a-box__products-container');
        if (!container) return;

        const exiting = container.querySelector('.build-a-box__products-grid');
        if (exiting) {
            exiting.classList.add('build-a-box__products-grid--exit');
            const onExitEnd = () => {
                exiting.removeEventListener('transitionend', onExitEnd);
                this.renderToContainer('.build-a-box__products-container', this.renderProductGrid());
                const entering = container.querySelector('.build-a-box__products-grid');
                if (entering) {
                    entering.classList.add('build-a-box__products-grid--enter');
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            entering.classList.remove('build-a-box__products-grid--enter');
                            // Ensure input values are restored after grid re-render
                            this.updateProductCards();
                        });
                    });
                }
            };
            exiting.addEventListener('transitionend', onExitEnd, { once: true });
            return;
        }
        this.renderToContainer('.build-a-box__products-container', this.renderProductGrid());
        // Ensure input values are restored after grid re-render
        setTimeout(() => {
            this.updateProductCards();
        }, 0);
    }

    updateSidebar() {
        this.renderToContainer('.build-a-box__sidebar-container', this.renderSidebar());
        if (this.state.delivery_modal_open) {
            this.initTouchHandlers();
        }
    }

    updatePricing() {
        this.updateSidebar();
    }

    formatPrice(cents) {
        return formatMoney(cents, this.moneyFormat);
    }

    truncateText(text, maxLength) {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
      }

      decodeHtmlEntities(text) {
        if (!text) return text;
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
      }

    toggleMessageDropdown(wrapper) {
        if (!wrapper) return;
        const content = wrapper.querySelector('.js-content-toggle-build-a-box__message-content-text');
        if (!content) return;

        const isOpen = wrapper.classList.contains('build-a-box__message-content--open');

        if (!isOpen) {
            content.style.height = content.scrollHeight + 'px';
            wrapper.classList.add('build-a-box__message-content--open');
            const onEnd = () => {
                content.style.height = 'auto';
                content.removeEventListener('transitionend', onEnd);
            };
            content.addEventListener('transitionend', onEnd, { once: true });
            return;
        }

        const current = content.offsetHeight;
        content.style.height = current + 'px';
        requestAnimationFrame(() => {
            content.style.height = '0px';
        });
        const onEnd = () => {
            wrapper.classList.remove('build-a-box__message-content--open');
            content.style.height = '';
            content.removeEventListener('transitionend', onEnd);
        };
        content.addEventListener('transitionend', onEnd, { once: true });
    }

    animateSidebarHeaderToggle(shouldOpen) {
        const header = this.querySelector('.build-a-box__sidebar-header');
        const list = header?.querySelector('.build-a-box__sidebar-products');
        if (!header || !list) {
            this.stateManager.updateField('sidebar_products_open', shouldOpen);
            return;
        }

        const toggleButton = header.querySelector('.build-a-box__sidebar-toggle');
        if (toggleButton) {
            toggleButton.textContent = shouldOpen
                ? this.getTranslation('close_choices')
                : this.getTranslation('see_choices');
        }

        const onTransitionEnd = () => {
            list.style.height = '';
            list.removeEventListener('transitionend', onTransitionEnd);
            this.stateManager.updateField('sidebar_products_open', shouldOpen);
        };

        const targetHeight = shouldOpen ? list.scrollHeight : list.clientHeight;
        if (!targetHeight) {
            header.classList.toggle('build-a-box__sidebar-header--open', shouldOpen);
            list.classList.toggle('build-a-box__sidebar-products--open', shouldOpen);
            this.stateManager.updateField('sidebar_products_open', shouldOpen);
            return;
        }

        if (shouldOpen) {
            header.classList.add('build-a-box__sidebar-header--open');
            list.classList.add('build-a-box__sidebar-products--open');
            list.style.height = '0px';
            void list.offsetHeight;
            list.style.height = list.scrollHeight + 'px';
        } else {
            list.style.height = list.scrollHeight + 'px';
            void list.offsetHeight;
            list.style.height = '0px';
            header.classList.remove('build-a-box__sidebar-header--open');
            list.classList.remove('build-a-box__sidebar-products--open');
        }

        list.addEventListener('transitionend', onTransitionEnd, { once: true });
    }

    initBottomSheet() {
        if (this.bottomSheetInitialized) return;
        this.bottomSheetInitialized = true;
        
        this.initTouchHandlers();
    }

    initTouchHandlers() {
        const handle = this.querySelector('.build-a-box__sidebar-handle');
        if (!handle || !this.state.delivery_modal_open) return;

        let startY = 0;
        let currentY = 0;
        let isDragging = false;

        const handleTouchStart = (e) => {
            startY = e.touches[0].clientY;
            isDragging = true;
            document.body.style.overflow = 'hidden';
        };

        const handleTouchMove = (e) => {
            if (!isDragging) return;
            currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;
            if (deltaY > 0) {
                const sidebar = this.querySelector('.build-a-box__sidebar');
                sidebar.style.transform = `translateY(${deltaY}px)`;
            }
        };

        const handleTouchEnd = (e) => {
            if (!isDragging) return;
            isDragging = false;
            const deltaY = currentY - startY;
            
            if (deltaY > 100) {
                this.closeSidebar();
            } else {
                const sidebar = this.querySelector('.build-a-box__sidebar');
                sidebar.style.transform = '';
            }
            document.body.style.overflow = '';
        };

        handle.addEventListener('touchstart', handleTouchStart);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
    }

    openDeliveryBottomSheet() {
        const isMinimumMet = this.stateManager.isMinimumMet();
        if (!isMinimumMet || this.state.total_items === 0) {
            return;
        }

        if (this.isMobile()) {
            if (!this.checkRemindersBeforeCart()) {
                return;
            }
        }

        this.stateManager.updateState({ sidebar_visible: true, delivery_modal_open: true });
        this.render();
        this.initTouchHandlers();
        const sidebar = this.querySelector('.build-a-box__sidebar');
        if (sidebar) {
            sidebar.style.transform = 'translateY(100%)';
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    sidebar.style.transform = '';
                });
            });
        }
    }

    closeSidebar() {
        const sidebar = this.querySelector('.build-a-box__sidebar');
        if (sidebar) {
            const onEnd = () => {
                sidebar.removeEventListener('transitionend', onEnd);
                this.stateManager.updateState({ sidebar_visible: false, delivery_modal_open: false });
                this.render();
            };
            sidebar.addEventListener('transitionend', onEnd, { once: true });
            // trigger close animation
            sidebar.style.transform = 'translateY(100%)';
            return;
        }
        this.stateManager.updateState({ sidebar_visible: false, delivery_modal_open: false });
        this.render();
    }
}

customElements.define('build-a-box', BuildABox);    
}